import { type SetStateAction } from 'react';
import { create } from 'zustand';

const filterRecordByIds = (record: Record<string, string>, validCommentIds: Set<string>) =>
  Object.fromEntries(Object.entries(record).filter(([commentId]) => validCommentIds.has(commentId)));

type CommentStore = {
  deletingCommentId: string | null;
  editingCommentId: string | null;
  activeReplyComposerCommentId: string | null;
  draftByCommentId: Record<string, string>;
  setDeletingCommentId: (value: SetStateAction<string | null>) => void;
  setEditingCommentId: (value: SetStateAction<string | null>) => void;
  setActiveReplyComposerCommentId: (value: SetStateAction<string | null>) => void;
  setCommentDraft: (commentId: string, draft: string) => void;
  clearCommentDraft: (commentId: string) => void;
  pruneCommentState: (validCommentIds: Array<string>) => void;
};

export const useCommentStore = create<CommentStore>((set) => ({
  deletingCommentId: null,
  editingCommentId: null,
  activeReplyComposerCommentId: null,
  draftByCommentId: {},
  setDeletingCommentId: (value) =>
    set((state) => ({
      deletingCommentId: typeof value === 'function' ? value(state.deletingCommentId) : value,
    })),
  setEditingCommentId: (value) =>
    set((state) => ({
      editingCommentId: typeof value === 'function' ? value(state.editingCommentId) : value,
    })),
  setActiveReplyComposerCommentId: (value) =>
    set((state) => ({
      activeReplyComposerCommentId: typeof value === 'function' ? value(state.activeReplyComposerCommentId) : value,
    })),
  setCommentDraft: (commentId, draft) =>
    set((state) => ({
      draftByCommentId: {
        ...state.draftByCommentId,
        [commentId]: draft,
      },
    })),
  clearCommentDraft: (commentId) =>
    set((state) => {
      const nextDraftByCommentId = { ...state.draftByCommentId };
      delete nextDraftByCommentId[commentId];

      return { draftByCommentId: nextDraftByCommentId };
    }),
  pruneCommentState: (validCommentIds) =>
    set((state) => {
      const validCommentIdSet = new Set(validCommentIds);

      return {
        editingCommentId:
          state.editingCommentId && validCommentIdSet.has(state.editingCommentId) ? state.editingCommentId : null,
        activeReplyComposerCommentId:
          state.activeReplyComposerCommentId && validCommentIdSet.has(state.activeReplyComposerCommentId)
            ? state.activeReplyComposerCommentId
            : null,
        draftByCommentId: filterRecordByIds(state.draftByCommentId, validCommentIdSet),
      };
    }),
}));
